/**
 * Quickshop on collection cards (snippets/card-quickshop.liquid).
 *
 * Without JavaScript every chip (its own form, with its own properties[EAN]) posts its variant to
 * /cart/add. This turns that into an in-page add through the shared helper (assets/cart-add.js): post to /cart/add.js with the cart drawer's
 * sections, then announce the new line with a CartLinesUpdateEvent. The theme's own listeners
 * take it from there: the drawer re-renders and opens with its confirmation line, and the header
 * bubble updates.
 *
 * One listener on the document, so cards that "Toon meer" or a filter change swap in later work
 * without being wired up again.
 *
 * The chip row is also fitted here: a card only has room for two rows of chips, so the chips that
 * do not fit are hidden and the "+N" link becomes the last chip, measured against the real card
 * width (ResizeObserver) rather than a fixed count. Without JavaScript nothing is clipped: all
 * chips wrap and the link stays below the row.
 */
import { addToCart } from '@theme/cart-add';

const ADD_ERROR = 'Toevoegen lukte niet, probeer het opnieuw.';

/**
 * The card a quickshop form belongs to: chips are one form each, inside the card, and the status
 * line and the other chips live in the card. A form that is the card itself (single add) is its own.
 *
 * @param {HTMLFormElement} form
 * @returns {Element}
 */
const cardOf = (form) => form.closest('.card-quickshop') ?? form;

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 * @param {boolean} isError - Errors show on the card; a success is only announced, the drawer
 *   already shows it.
 */
const setStatus = (form, message, isError) => {
  const status = cardOf(form).querySelector('[data-quickshop-status]');
  if (!status) return;
  status.classList.toggle('card-quickshop__status--error', isError);
  status.classList.toggle('visually-hidden', !isError);
  status.textContent = message;
};

/**
 * Locks the card's chips while one add is in flight, so a second click cannot add twice (the
 * submit handler checks data-quickshop-busy). aria-disabled rather than disabled: a disabled
 * chip would drop keyboard focus to the body, and the cart drawer would then hand focus back
 * there on close instead of to the chip. Sold-out chips ship disabled and are not touched.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement} submitter
 * @param {boolean} busy
 */
const setBusy = (form, submitter, busy) => {
  const card = cardOf(form);
  card.toggleAttribute('data-quickshop-busy', busy);

  if (busy) submitter.setAttribute('aria-busy', 'true');
  else submitter.removeAttribute('aria-busy');

  card.querySelectorAll('button[type="submit"]').forEach((button) => {
    if (busy) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  });
};

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-card-quickshop')) return;

  // Browsers without event.submitter keep the native post, which adds the same variant.
  const submitter = event.submitter;
  if (!(submitter instanceof HTMLButtonElement) || !submitter.value) return;

  event.preventDefault();
  if (cardOf(form).hasAttribute('data-quickshop-busy')) return;

  const label = submitter.dataset.quickshopLabel ?? '';

  setStatus(form, '', false);
  setBusy(form, submitter, true);

  try {
    const { redirect } = await addToCart(form, { submitter, source: 'card-quickshop' });
    if (!redirect) setStatus(form, `${label} is toegevoegd aan je winkelwagen`, false);
  } catch (reason) {
    // A definite refusal (just sold out, or the whole stock is already in the cart) carries
    // Shopify's own message; nothing changed, so the drawer stays as it is and the card says why.
    setStatus(form, reason instanceof Error && reason.message ? reason.message : ADD_ERROR, true);
  } finally {
    setBusy(form, submitter, false);
  }
});

const MAX_ROWS = 2;
const FALLBACK_GAP = 8;

/**
 * How many chips fit in `maxRows` rows of a wrapping flex row. When chips are left out (or
 * `reserveMore` says the "+N" chip must show anyway), the "+N" chip takes a place in the last row.
 * A chip wider than the row is squeezed to the row width (the CSS truncates its label).
 *
 * @param {object} input
 * @param {number[]} input.widths - Border-box width of every chip, in order.
 * @param {number} input.moreWidth - Width of the "+N" chip.
 * @param {number} input.containerWidth - Inner width of the row.
 * @param {number} input.gap - Column gap between chips.
 * @param {boolean} [input.reserveMore] - The "+N" chip shows even when every chip fits.
 * @param {number} [input.maxRows]
 * @returns {number} The number of leading chips to keep visible.
 */
export const countFittingChips = ({ widths, moreWidth, containerWidth, gap, reserveMore = false, maxRows = MAX_ROWS }) => {
  /** @param {number[]} list */
  const rowsNeeded = (list) => {
    let rows = 0;
    let x = 0;
    for (const width of list) {
      const w = Math.min(width, containerWidth);
      if (rows === 0) {
        rows = 1;
        x = w;
      } else if (x + gap + w > containerWidth) {
        rows += 1;
        x = w;
      } else {
        x += gap + w;
      }
    }
    return rows;
  };

  if (!reserveMore && rowsNeeded(widths) <= maxRows) return widths.length;

  for (let keep = reserveMore ? widths.length : widths.length - 1; keep >= 0; keep--) {
    if (rowsNeeded([...widths.slice(0, keep), moreWidth]) <= maxRows) return keep;
  }
  return 0;
};

/**
 * Fits one chip row: hides the chips that do not fit in two rows and writes the "+N" link.
 * Everything is un-hidden first so the widths are measured from the full row.
 *
 * @param {HTMLElement} row - A .card-quickshop__chips element.
 */
export const fitCard = (row) => {
  const chips = Array.from(row.querySelectorAll('.card-quickshop__chip'));
  const more = row.closest('.card-quickshop')?.querySelector('[data-quickshop-more]');
  if (chips.length === 0 || !(more instanceof HTMLElement)) return;

  const style = getComputedStyle(row);
  const containerWidth = row.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
  // A card that is not laid out (display none, zero width) is measured again once it is.
  if (containerWidth <= 0) return;

  const label = more.querySelector('[data-quickshop-more-label]');
  const extra = Number(more.dataset.extra) || 0;

  // Into the row as the last chip, showing its widest text, so its width is measured right.
  more.classList.add('card-quickshop__more--inline');
  row.append(more);
  more.hidden = false;
  if (label) label.textContent = `+${chips.length + extra}`;
  for (const chip of chips) chip.hidden = false;

  const keep = countFittingChips({
    widths: chips.map((chip) => chip.getBoundingClientRect().width),
    moreWidth: more.getBoundingClientRect().width,
    containerWidth,
    gap: parseFloat(style.columnGap) || FALLBACK_GAP,
    reserveMore: extra > 0,
  });

  chips.forEach((chip, index) => {
    chip.hidden = index >= keep;
  });

  const hiddenCount = chips.length - keep + extra;
  if (hiddenCount > 0) {
    if (label) label.textContent = `+${hiddenCount}`;
  } else {
    more.hidden = true;
  }
};

/** Every chip row on the page, and the width each was last fitted at. */
const rows = new Set();
let lastWidths = new WeakMap();

/** Fits every chip row again, e.g. once the web fonts have changed the chip widths. */
const refitAll = () => {
  lastWidths = new WeakMap();
  rows.forEach((row) => (row.isConnected ? fitCard(row) : rows.delete(row)));
};

if (typeof ResizeObserver !== 'undefined') {
  // From here on the script fits the rows, so CSS may clip them (see .quickshop-fit in base.css).
  document.documentElement.classList.add('quickshop-fit');

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const row = entry.target;
      const width = entry.contentRect.width;
      // Hiding chips only changes the height: fit again when the width changed.
      if (!(row instanceof HTMLElement) || lastWidths.get(row) === width) continue;
      lastWidths.set(row, width);
      fitCard(row);
    }
  });

  /** Drops rows that left the document (a filter swapped the grid) so they can be collected. */
  const forgetDetachedRows = () => {
    rows.forEach((row) => {
      if (row.isConnected) return;
      rows.delete(row);
      resizeObserver.unobserve(row);
    });
  };

  const observeNewRows = () => {
    document.querySelectorAll('.card-quickshop__chips').forEach((row) => {
      if (rows.has(row)) return;
      rows.add(row);
      resizeObserver.observe(row);
    });
  };

  observeNewRows();

  // Cards that "Toon meer" or a filter change bring in later.
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      forgetDetachedRows();
      observeNewRows();
    });
  }).observe(document.body, { childList: true, subtree: true });

  document.fonts?.ready.then(refitAll);
}
